import React, { useEffect, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";
import PropTypes from "prop-types";
import {
  MENU_CLASS, optionClass, useAnchoredPosition, useOutsideClose,
  useListboxKeys, useScrollActiveIntoView,
} from "./dropdownKit";

// Picker chọn 1 user — search server (debounce 350ms), popover render qua PORTAL.
// Popover dùng position:fixed + createPortal(document.body) để KHÔNG bị cắt bởi
// card cha (overflow-hidden) hay cột form (overflow-y-auto) — giống SelectDropdown/InvoicePicker.
// Props:
//   value: { id, email, name } | null
//   onChange(user|null)
//   fetcher(q): Promise<{ data: [{ value, email, label }] }>
//   placeholder
const initials = (s) => (String(s || "?").trim().slice(0, 2) || "?").toUpperCase();

const UserPicker = ({ value, onChange, fetcher, placeholder = "Select a person...", 'aria-label': ariaLabel }) => {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const btnRef = useRef(null);
  const popRef = useRef(null);
  const listRef = useRef(null);
  const inputRef = useRef(null);

  // Search server (debounce) — chỉ chạy khi mở.
  useEffect(() => {
    if (!open || !fetcher) return undefined;
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await fetcher(q);
        setOptions(res?.data || []);
      } catch {
        setOptions([]);
      } finally {
        setLoading(false);
      }
    }, 350);
    return () => clearTimeout(t);
  }, [q, open, fetcher]);

  const close = () => { setOpen(false); setQ(""); };
  useOutsideClose(open, [btnRef, popRef], close);

  // Autofocus ô tìm kiếm khi mở (bàn phím vào thẳng việc gõ tên).
  useEffect(() => {
    if (!open) return undefined;
    const t = setTimeout(() => inputRef.current?.focus(), 0);
    return () => clearTimeout(t);
  }, [open]);


  const pick = (idx) => {
    const o = options[idx];
    if (!o) return;
    onChange({ id: o.value, email: o.email, name: (o.label || "").split(" — ")[0] });
    setOpen(false);
    setQ("");
    btnRef.current?.focus();
  };

  const { activeIndex, setActiveIndex, onKeyDown } = useListboxKeys({
    open, setOpen, count: options.length, onSelect: pick, anchorRef: btnRef,
  });
  useScrollActiveIntoView(listRef, activeIndex);

  const coords = useAnchoredPosition(open, btnRef, { minWidth: 280 });
  const listId = `pr-user-${useId()}`;

  return (
    <>
      {/* Trigger — underline/box style (đồng bộ với các field khác trong form) */}
      <div
        ref={btnRef}
        role="combobox"
        aria-label={ariaLabel || placeholder}
        tabIndex={0}
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-controls={open ? listId : undefined}
        onKeyDown={onKeyDown}
        onClick={() => setOpen((o) => !o)}
        className={`tw-w-full tw-h-11 tw-flex tw-items-center tw-gap-2 tw-rounded-geist-sm tw-border tw-border-solid tw-bg-geist-bg tw-px-3 tw-text-sm tw-cursor-pointer tw-transition-colors focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-geist-accent/20 ${
          open ? "tw-border-geist-accent" : "tw-border-geist-border hover:tw-border-geist-border-2"}`}
      >
        {value?.email && (
          <span className="tw-inline-flex tw-h-6 tw-w-6 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-xl tw-bg-geist-100 tw-text-geist-fg-2 tw-text-[10px] tw-font-semibold">
            {initials(value.name || value.email)}
          </span>
        )}
        <span className={`tw-flex-1 tw-min-w-0 tw-truncate ${value?.email ? "tw-text-geist-fg" : "tw-text-geist-fg-3"}`}>
          {value?.email ? `${value.name || ""} · ${value.email}` : placeholder}
        </span>
        {value?.email && (
          <i
            role="button"
            title="Clear"
            className="bi bi-x-lg tw-text-[11px] tw-text-geist-fg-3 hover:tw-text-geist-error"
            onClick={(e) => { e.stopPropagation(); onChange(null); }}
          />
        )}
        <i className={`bi ${open ? "bi-chevron-up" : "bi-chevron-down"} tw-text-[11px] tw-text-geist-fg-3 tw-shrink-0`} />
      </div>

      {open && coords && createPortal(
        <div
          ref={popRef}
          style={{ position: "fixed", zIndex: 100060, ...coords, maxHeight: undefined, width: coords.width }}
          className={MENU_CLASS}
          onKeyDown={onKeyDown}
          role="presentation"
        >
          <div className="tw-p-2 tw-border-0 tw-border-b tw-border-solid tw-border-geist-border tw-bg-geist-bg-2">
            <div className="tw-flex tw-items-center tw-gap-2 tw-bg-geist-bg tw-border tw-border-solid tw-border-geist-border tw-rounded-geist-sm tw-px-2.5 tw-py-1.5 focus-within:tw-border-geist-accent">
              <i className="bi bi-search tw-text-[11px] tw-text-geist-fg-3 tw-flex-shrink-0" />
              <input
                ref={inputRef}
                autoComplete="off"
                value={q}
                onChange={(e) => { setQ(e.target.value); setActiveIndex(0); }}
                placeholder="Search by name / email..."
                className="tw-flex-1 tw-min-w-0 tw-bg-transparent tw-border-0 tw-outline-none tw-text-sm tw-text-geist-fg placeholder:tw-text-geist-fg-3"
              />
            </div>
          </div>
          <div ref={listRef} id={listId} role="listbox" style={{ maxHeight: coords.maxHeight }}
            className="tw-overflow-y-auto tw-py-1">
            {loading ? (
              <div className="tw-px-3 tw-py-4 tw-text-sm tw-text-geist-fg-3 tw-flex tw-items-center tw-gap-2">
                <i className="bi bi-arrow-repeat tw-animate-spin" /> Searching...
              </div>
            ) : options.length === 0 ? (
              <div className="tw-px-3 tw-py-4 tw-text-sm tw-text-geist-fg-3 tw-text-center">
                {q.trim() ? "No matching person." : "Type a name or email to search."}
              </div>
            ) : (
              options.map((o, idx) => {
                const name = (o.label || "").split(" — ")[0];
                const selected = !!value?.email && value.email === o.email;
                return (
                  <div
                    key={o.value}
                    data-index={idx}
                    role="option"
                    aria-selected={selected}
                    tabIndex={-1}
                    onMouseEnter={() => setActiveIndex(idx)}
                    onClick={() => pick(idx)}
                    className={optionClass(selected, activeIndex === idx)}
                  >
                    <span className="tw-inline-flex tw-h-7 tw-w-7 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-xl tw-bg-geist-100 tw-text-geist-fg-2 tw-text-[11px] tw-font-semibold">
                      {initials(name || o.email)}
                    </span>
                    <span className="tw-flex-1 tw-min-w-0">
                      <span className="tw-block tw-text-sm tw-font-semibold tw-truncate">{name || o.email}</span>
                      <span className="tw-block tw-text-[12px] tw-font-normal tw-text-geist-fg-3 tw-truncate">{o.email}</span>
                    </span>
                    {selected && <i className="bi bi-check2 tw-text-geist-accent tw-text-[13px] tw-shrink-0" />}
                  </div>
                );
              })
            )}
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

UserPicker.propTypes = {
  value: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    email: PropTypes.string,
    name: PropTypes.string,
  }),
  onChange: PropTypes.func.isRequired,
  fetcher: PropTypes.func,
  placeholder: PropTypes.string,
  'aria-label': PropTypes.string,
};

export default UserPicker;
